import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {useAuth} from '../Contexts/AuthContext';
import { warn, error as logError } from "../utils/logger";
import { API_BASE_URL, WS_BASE_URL } from './TokenChecker';

const priorityColors = {
  Immediate: 'border-t-4 border-red-500',
  Moderate: 'border-t-4 border-yellow-500',
  Lenient: 'border-t-4 border-green-500',
};

// Base URLs used across effects and handlers

const CommentBox = ({ taskId, userEmail, title, priority, onClose }) => {
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const { authData } = useAuth();
  const commentsEndRef = useRef(null);


  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    // Handle ISO timestamp format from Django
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      warn('Invalid timestamp format:', timestamp);
      return 'Invalid';
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const timePart = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    
    // Calculate difference in days
    const diffTime = today.getTime() - messageDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today - just show time
      return timePart;
    } else if (diffDays === 1) {
      // Yesterday
      return `Yesterday ${timePart}`;
    } else if (diffDays < 7) {
      // This week - show day name
      const dayName = date.toLocaleDateString([], { weekday: 'long' });
      return `${dayName} ${timePart}`;
    } else if (diffDays < 365) {
      // This year - show month and day
      const monthDay = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return `${monthDay} ${timePart}`;
    } else {
      // Older than a year - show full date
      const fullDate = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      return `${fullDate} ${timePart}`;
    }
  };

  useEffect(() => {
  commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
}, [comments]);


  useEffect(() => {
    let isMounted = true;
    
    // Fetch existing comments once
    const fetchComments = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/task_comments/${taskId}`);
        const data = await res.json();
        if (isMounted) {
          setComments(data.comments || []);
        }
      } catch (error) {
        logError('Error fetching comments:', error);
        if (isMounted) {
          setError('Failed to load comments');
        }
      }
    };

    fetchComments();

    // Create WebSocket connection
    const connectWebSocket = () => {
      if (socketRef.current?.readyState === WebSocket.CONNECTING || 
          socketRef.current?.readyState === WebSocket.OPEN) {
        return; // Already connecting or connected
      }

      socketRef.current = new WebSocket(`${WS_BASE_URL}/ws/comments/${taskId}/`);

      socketRef.current.onopen = () => {
        // connection established
        if (isMounted) {
          setIsConnected(true);
          setError('');
        }
      };

      socketRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.comment && isMounted) {
          // Only add comments from other users to avoid duplicates
          if (data.comment.user !== authData?.email) {
            setComments(prev => {
              // Check if comment already exists by unique identifier
              const exists = prev.some(c => 
                c.id === data.comment.id || 
                (c.content === data.comment.content && 
                 c.user === data.comment.user && 
                 c.timestamp === data.comment.timestamp)
              );
              if (!exists) {
                return [...prev, data.comment];
              }
              return prev;
            });
          }
        }
      };

      socketRef.current.onclose = () => {
        if (isMounted) {
          setIsConnected(false);
          // Clear any existing timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMounted) {
              connectWebSocket();
            }
          }, 3000);
        }
      };

      socketRef.current.onerror = (error) => {
        logError('WebSocket error:', error);
        if (isMounted) {
          setIsConnected(false);
          setError('Connection error');
        }
      };
    };

    connectWebSocket();

    // Cleanup
    return () => {   
      isMounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [taskId, authData?.email]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setError('');
    const commentData = {
      task_id: taskId,
      user_email: userEmail,
      content: input.trim(),
    };

    try {
      // Save comment to database via REST API
      const res = await fetch(`${API_BASE_URL}/api/add_comment/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commentData),
      });

      if (res.ok) {
        const savedComment = await res.json();
        
        // Add comment to local state immediately
        setComments(prev => [...prev, savedComment]);
        
        // Notify other users via WebSocket (don't send back to self)
        if (isConnected && socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({
            comment: savedComment
          }));
        }
        
        setInput('');
      } else {
        const errorData = await res.json();
        setError(errorData.message || 'Failed to save comment');
      }
    } catch (error) {
      logError('Error sending comment:', error);
      setError('Network error. Please try again.');
    }
  };

  const isMyComment = (comment) => {
    // Now we can simply compare emails since 'user' field contains the email
    return authData?.email === comment.user;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center border-gray-400">
      <div className={`w-full max-w-md bg-white rounded-lg shadow-lg relative ${priorityColors[priority] || 'border-t-4 border-[#4299E1]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold text-[#2D3748]">{title}</h3>
          <div className="flex items-center gap-2">
            {/* Connection status indicator */}
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} 
                 title={isConnected ? 'Connected' : 'Disconnected'} />
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Comments */}
        <div className="px-3 py-2 max-h-64 min-h-44 bg-[#F7FAFC] overflow-y-auto space-y-2">
          {comments.map((c, i) => {
            const isCurrentUser = isMyComment(c);
            // Create a more unique key
            const commentKey = c.id || `${c.user}-${c.timestamp}-${i}`;
            
            return (
              <div
                key={commentKey}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-2`}
              >
                <div
                  className={`max-w-[80%] ${
                    isCurrentUser 
                      ? "bg-blue-500 text-white rounded-2xl rounded-br-md" 
                      : "bg-white border border-gray-200 rounded-2xl rounded-bl-md"
                  } px-3 py-2 shadow-sm`}
                >
                  {!isCurrentUser && (
                    <div className="text-xs font-medium text-gray-600 mb-1">
                      {c.name || c.user?.split('@')[0]}
                    </div>
                  )}
                  <div className={`text-sm leading-relaxed ${isCurrentUser ? "text-white" : "text-gray-800"}`}>
                    {c.content}
                  </div>
                  <div className={`text-xs mt-1 ${isCurrentUser ? "text-blue-200" : "text-gray-400"} text-right`}>
                    {formatTimestamp(c.timestamp)}
                  </div>
                </div>
              </div>
            )
          })}
          {comments.length === 0 && (
            <div className="text-center text-gray-500 text-sm py-8">
              No comments yet. Be the first to add one!
            </div>
          )}
           <div ref={commentsEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleComment} className="px-4 py-3 border-t flex items-center space-x-2">
          <input
            className="flex-1 text-sm p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
            placeholder={isConnected ? "Write a comment..." : "Connecting..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!isConnected}
          />
          <button
            type="submit"
            disabled={!input.trim() || !isConnected}
            className="text-sm px-3 py-2 bg-[#4299E1] text-white rounded-md hover:bg-[#3182CE] disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentBox;
